import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../lib/api-client';
import type {
  DashboardEnforcementDto,
  DashboardFacilityTypePerformanceDto,
  DashboardFollowupsDto,
  DashboardLocationRankingDto,
  DashboardOverviewDto,
  DashboardTrendsDto,
} from '../types/api';

const TEN_MINUTES = 10 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

type CommonFilters = {
  month?: string;
  type?: 'HYGIENE' | 'COMPLIANCE';
  facilityTypeId?: string;
  geoLocationId?: string;
};

export const useDashboardOverview = (params?: CommonFilters) => {
  return useQuery<DashboardOverviewDto>({
    queryKey: ['dashboard-overview', params],
    queryFn: () => dashboardApi.getOverview(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useDashboardTrends = (params?: {
  granularity?: 'daily' | 'weekly' | 'monthly';
  from?: string;
  to?: string;
  type?: 'HYGIENE' | 'COMPLIANCE';
  facilityTypeId?: string;
  geoLocationId?: string;
}) => {
  return useQuery<DashboardTrendsDto>({
    queryKey: ['dashboard-trends', params],
    queryFn: () => dashboardApi.getTrends(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useDashboardEnforcement = (params?: CommonFilters) => {
  return useQuery<DashboardEnforcementDto>({
    queryKey: ['dashboard-enforcement', params],
    queryFn: () => dashboardApi.getEnforcement(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useDashboardFacilityTypePerformance = (params?: {
  month?: string;
  type?: 'HYGIENE' | 'COMPLIANCE';
  geoLocationId?: string;
}) => {
  return useQuery<DashboardFacilityTypePerformanceDto>({
    queryKey: ['dashboard-facility-type-performance', params],
    queryFn: () => dashboardApi.getFacilityTypePerformance(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useDashboardFollowups = (params?: {
  month?: string;
  type?: 'HYGIENE' | 'COMPLIANCE';
  facilityTypeId?: string;
  geoLocationId?: string;
}) => {
  return useQuery<DashboardFollowupsDto>({
    queryKey: ['dashboard-followups', params],
    queryFn: () => dashboardApi.getFollowups(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

export const useDashboardLocationRanking = (params?: {
  month?: string;
  type?: 'HYGIENE' | 'COMPLIANCE';
  locationType?: string;
  facilityTypeId?: string;
  geoLocationId?: string;
  limit?: number;
}) => {
  return useQuery<DashboardLocationRankingDto>({
    queryKey: ['dashboard-location-ranking', params],
    queryFn: () => dashboardApi.getLocationRanking(params),
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};
